import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ENV } from "@app/env";
import { Store } from "@ngrx/store";
import { AngularFireDatabase } from "angularfire2/database";
import { AngularFirestore } from "angularfire2/firestore";
import * as firebase from "firebase/app";
import { Observable } from "rxjs/Observable";
import { of } from "rxjs/observable/of";
import { switchMap, take } from "rxjs/operators";
import { isValidEventId } from "../helpers/utils.helper";
import { ICategory } from "../shared/models/categories.model";
import { IClientModules } from "../shared/models/client.model";
import { IFormsUsers } from "../shared/models/form-users.model";
import { IProfileUsers } from "../shared/models/profile-users.model";
import * as sharedSelectors from "../shared/reducers/shared.reducer";
import { HistoryData } from "./history";
import { _TRACKING_ENTITIES, TrackingProvider } from "./tracking";
import { Utilities } from "./utilities";

@Injectable()
export class ClientData {
  private action: string = "update";

  constructor(
    private afDb: AngularFireDatabase,
    private afstore: AngularFirestore,
    private historyData: HistoryData,
    private trackingAi: TrackingProvider,
    private utilities: Utilities,
    private store: Store<{}>,
    private http: HttpClient,
  ) { }

  public getClients(): any {
    return this.afDb.list("ClientConfig").valueChanges();
  }

  public getResourcesByClientId(client: string): Observable<any[]> {
    return this.afDb
      .list<any>(`Clients/${client}/Interactivity/Resources`)
      .valueChanges();
  }

  public getUsersByClient(clientId: string): Observable<any[]> {
    return this.afDb
      .list<any>(`Clients/${clientId}/Users`)
      .valueChanges();
  }

  public getUserById(client: string, userId: string): Observable<any> {
    return this.afDb
      .object<any>(`Clients/${client}/Users/${userId}`)
      .valueChanges()
      .take(1);
  }

  public getResourcesById(client: string, resourceId): Observable<any> {
    return this.afDb
      .object<any>(`Clients/${client}/Interactivity/Resources/${resourceId}`)
      .valueChanges();
  }

  public getRaffleConfig(client: string): Observable<any> {
    return this.afDb
      .object<any>(`Clients/${client}/RaffleConfig`)
      .valueChanges()
      .take(1);
  }

  public getConfigPlanMyDay(clientId: string, eventId: string = null): Observable<any> {
    if (!isValidEventId(eventId)) {
      return this.afDb
        .object<any>(`ClientConfig/${clientId}/plan_my_day`)
        .valueChanges();
    } else {
      return this.afDb
        .object<any>(`Clients/${clientId}/EventConfig/${eventId}/plan_my_day`)
        .valueChanges();
    }
  }

  public createOrEditPlanMyDay(clientId: string, planInfo: any, eventId: string = null) {
    let ref = `ClientConfig/${clientId}/plan_my_day`;
    if (isValidEventId(eventId)) {
      ref = `Clients/${clientId}/EventConfig/${eventId}/plan_my_day`;
    }

    return new Promise((res, rej) => {
      this.afDb.database
        .ref(ref)
        .set(planInfo)
        .then(() => {
          return res(planInfo);
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public getUsersRaffle(clientId: string): Observable<any[]> {
    return this.afDb
      .list(`Clients/${clientId}/RafflePrivate`)
      .valueChanges();
    // TODO: Deshabilitado mienstras se pasa a firestore
    // return this.afstore.collection(clientId).doc('raffle_private').collection('users')
    //   .valueChanges();
  }

  public deleteUserRaffle(clientId: string, codeId: string): Promise<any> {
    return this.afstore.collection(clientId).doc("raffle_private").collection("users").doc(codeId)
      .delete();
  }

  public getGenericConfig(moduleName: string): Observable<any> {
    return this.store.select(sharedSelectors.getClientId).pipe(
      switchMap((clientId) => {
        if (clientId) {
          return this.afDb
            .object(`Clients/${clientId}/Interactivity/${moduleName}`)
            .valueChanges();
        } else {
          return of();
        }
      }),
    );
  }

  public createOrEditConfigGeneric(info: any, moduleName: string): Promise<any> {
    return this.store.select(sharedSelectors.getClientId).pipe(
      switchMap((clientId) => this.afDb.database
        .ref(`Clients/${clientId}/Interactivity/${moduleName}`)
        .set(info),
      ),
      take(1),
    ).toPromise();
  }

  public getRaffleRewards(client: string): Observable<any[]> {
    return this.afDb
      .list<any>(`Clients/${client}/RaffleReward`)
      .valueChanges();
  }

  public pushResource(
    client: string,
    object: any,
    id: string = "",
  ): any {
    if (id === "") {
      this.action = "create";
      id = this.afDb.createPushId();
      object.created_at = new Date().getTime();
    } else {
      object.update_at = new Date().getTime();
    }

    object.id = id;

    return new Promise((res, rej) => {
      this.afDb.database
        .ref(`Clients/${client}/Interactivity/Resources/${id}`)
        .update(object)
        .then(() => {
          return res(id);
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public getClientConfig(clientId): any {
    return firebase.database().ref(`/ClientConfig/${clientId}`);
  }

  public setClientConfigModule(client: string, object: any, module: string): any {
    let reference = `ClientConfig/${client}/modules_config/${module}`;

    return new Promise((res, rej) => {
      this.afDb.database
        .ref(reference)
        .update({
          ...object,
          id: module,
        })
        .then(() => {
          return res(client);
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public getClientConfigModule(client: string, module: string): any {
    let reference = `ClientConfig/${client}/modules_config/${module}`;
    return this.afDb.object(reference).valueChanges();
  }

  /**
   * Get all the client categories
   * @param clientId
   */
  public getClientCategories(clientId: string): Observable<ICategory[]> {
    return this.afDb.list<ICategory>(`/ClientConfig/${clientId}/categories`).valueChanges();
  }

  /**
   * Get the client categories where the state is true
   * @param clientId
   */
  public getClientCategoriesActive(clientId: string): Observable<any> {
    return this.afDb.list<ICategory>(`/ClientConfig/${clientId}/categories`, (ref) => {
      return ref.orderByChild("state").equalTo(true);
    }).valueChanges();
  }

  public getClientConfigObservable(clientId): Observable<any> {
    return this.afDb.object(`/ClientConfig/${clientId}`).valueChanges();
  }

  public getClientModules(clientId): Observable<IClientModules> {
    return this.afDb.object<IClientModules>(`/ClientConfig/${clientId}/modules`).valueChanges();
  }

  public getClientSponsors(clientId): Observable<any[]> {
    return this.afDb.list(`/ClientConfig/${clientId}/Sponsor`).valueChanges();
  }

  public getClientProfiles(clientId): Observable<IProfileUsers[]> {
    return this.afDb.list<IProfileUsers>(`/Clients/${clientId}/Profiles`).valueChanges();
  }

  public getClientFormUsers(clientId): Observable<IFormsUsers[]> {
    return this.afDb.list<IFormsUsers>(`/Clients/${clientId}/FormUsers`).valueChanges();
  }

  public getClientFormUser(clientId, formId): Observable<IFormsUsers> {
    return this.afDb.object<IFormsUsers>(`/Clients/${clientId}/FormUsers/${formId}`).valueChanges();
  }

  public getClientActiveProfiles(clientId): Observable<IProfileUsers[]> {
    return this.afDb.list<IProfileUsers>(
      `/Clients/${clientId}/Profiles`,
      (ref) => ref.orderByChild("state").equalTo(true),
    ).valueChanges();
  }

  public getClientSponsor(clientId, sponsorId): Observable<any> {
    return this.afDb.object(`/ClientConfig/${clientId}/Sponsor/${sponsorId}`).valueChanges();
  }

  public getClientStaffs(clientId): Observable<any[]> {
    return this.afDb.list(`/Clients/${clientId}/Staff`).valueChanges();
  }

  public getClientReferred(clientId): Observable<any> {
    return this.afDb.object(`/Clients/${clientId}/Referred`).valueChanges();
  }

  public getClientAuthConfig(clientId): Observable<any> {
    return this.afDb.object(`/ClientConfig/${clientId}/AuthConfig`).valueChanges();
  }

  public getClientBusinessFormList(clientId): Observable<any[]> {
    return this.afDb.list(`/Clients/${clientId}/Interactivity/Business/Form`).valueChanges();
  }

  public getClientBusinessAgendaFormList(clientId): Observable<any[]> {
    return this.afDb.list(`/Clients/${clientId}/Interactivity/Business/Agenda`).valueChanges();
  }

  public getClientInteractivityStaffs(clientId): Observable<any> {
    return this.afDb.object(`/Clients/${clientId}/Interactivity/Staff`).valueChanges();
  }

  public getClientStaff(clientId, staffId): Observable<any> {
    console.debug(`/Clients/${clientId}/Interactivity/Staff/${staffId}`)
    return this.afDb.object(`/Clients/${clientId}/Interactivity/Staff/${staffId}`).valueChanges();
  }

  public getClientQuestions(clientId: string): Observable<any[]> {
    return this.afDb.list(`/Clients/${clientId}/Interactivity/Question`).valueChanges();
  }

  public setQuestion(
    client: string,
    questionId: string,
    question: any,
  ) {
    question.createdAt = new Date().getTime();
    return new Promise((res, rej) => {
      this.afDb.database
        .ref(`Clients/${client}/Interactivity/Question/${questionId}/`)
        .update(question)
        .then(() => {
          return res();
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public setQuestionArchive(client: string, questionId: string) {
    return this.afDb.object(`/Clients/${client}/Interactivity/Question/${questionId}/inactive`).set(true);
  }

  public unSetQuestionArchive(client: string, questionId: string) {
    return this.afDb.object(`/Clients/${client}/Interactivity/Question/${questionId}/inactive`).set("");
  }

  public deleteQuestion(client: string, questionId: string) {
    return this.afDb.object(`/Clients/${client}/Interactivity/Question/${questionId}`).remove();
  }

  public getClientQuestionsPublic(clientId: string): Observable<any[]> {
    return this.afDb.list(`/Clients/${clientId}/Interactivity/Question`).valueChanges();
  }

  public async setClientConfig(object: any, clientId: string = "") {
    if (!clientId) {
      object.created_at = new Date().getTime();
      object.AuthConfig = {
        defaultDisplayName: "",
        defaultPassword: "",
        hiddenCreate: false,
        hiddenCustomLogin: false,
        hiddenFacebook: false,
        hiddenForgot: false,
        hiddenGoogle: false,
        hiddenLinkedin: false,
        hiddenName: false,
        hiddenPassword: false,
        hiddenTerms: false,
        interestDefault: false,
        isNetworking: false,
        terms: true,
        type_auth: "rebus",
      };
      clientId = this.afDb.createPushId();
      this.action = "create";
    } else {
      object.update_at = new Date().getTime();
    }

    if (Object.keys(object.categories).length === 0) {
      // Si el cliente es nuevo se insertan las categorias default
      object.categories = await this.utilities.getDefaultCategories();
    }

    object.id = clientId;

    return new Promise((res, rej) => {
      this.utilities.consoleDecorator("Object client", object);
      return firebase
        .database()
        .ref(`/ClientConfig/${clientId}`)
        .update(object)
        .then(() => {
          this.historyData.setClient(
            "General description",
            "client",
            this.action,
            object,
          );

          // El track solo se habilita si ya cuenta con categorias creadas
          if (object.categories) {
            this.trackingAi.trackEntity(_TRACKING_ENTITIES.client, clientId, clientId, null, object, "Tracking cuando se crea o actualiza la configuracion del cliente");
          }
          res(clientId);
        })
        .catch((e) => {
          rej(e);
        });
    });
  }

  public createProfileUsers(client: string, profile: IProfileUsers) {
    if (!profile.id) {
      this.action = "create";
      profile.id = this.afDb.createPushId();
    } else {
      this.action = "update";
    }

    return new Promise((resolve, reject) => {
      return this.afDb.database.ref(`Clients/${client}/Profiles/${profile.id}`)
        .update(profile)
        .then(() => {
          return this.historyData.set(
            `Profile content`,
            "profile_users",
            this.action,
            profile,
          );
        })
        .then(() => {
          resolve(profile);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public createBusinessForm(clientId: string, business: any) {
    if (!business.id) {
      this.action = "create";
      business.id = this.afDb.createPushId();
    } else {
      this.action = "update";
    }

    return new Promise((resolve, reject) => {
      return this.afDb.database.ref(`Clients/${clientId}/Interactivity/Business/Form/${business.id}`)
        .update(business)
        .then(() => {
          return this.historyData.set(
            `Business content`,
            "business_form",
            this.action,
            business,
          );
        })
        .then(() => {
          resolve(business);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public clearBusinessAgendaForm(clientId: string) {
    return this.afDb.database.ref(`Clients/${clientId}/Interactivity/Business/Agenda`).remove();
  }

  public createBusinessAgendaForm(clientId: string, business: any) {
    if (!business.id) {
      this.action = "create";
      business.id = this.afDb.createPushId();
    } else {
      this.action = "update";
    }

    return new Promise((resolve, reject) => {
      return this.afDb.database.ref(`Clients/${clientId}/Interactivity/Business/Agenda/${business.id}`)
        .update(business)
        .then(() => {
          return this.historyData.set(
            `Business agenda content`,
            "business_form",
            this.action,
            business,
          );
        })
        .then(() => {
          resolve(business);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public createFormUsers(client: string, formData: IFormsUsers) {
    if (!formData.id) {
      this.action = "create";
      formData.id = this.afDb.createPushId();
    } else {
      this.action = "update";
    }

    return new Promise((resolve, reject) => {
      this.afDb.database.ref(`Clients/${client}/FormUsers/${formData.id}`)
        .update(formData)
        .then(() => {
          return this.historyData.set(
            `Form Users content`,
            "form_users",
            this.action,
            formData,
          );
        })
        .then(() => {
          resolve(formData);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public setClientAuthConfig(object: any, clientId: string): any {
    object.update_at = new Date().getTime();

    return new Promise((res, rej) => {
      return firebase
        .database()
        .ref(`/ClientConfig/${clientId}/AuthConfig`)
        .update(object)
        .then(() => {
          res(clientId);
        })
        .catch((e) => {
          rej(e);
        });
    });
  }

  public setRaffleReward(object: any, clientId: string): Promise<any> {
    return new Promise((res, rej) => {
      return firebase
        .database()
        .ref(`/Clients/${clientId}/RaffleReward/${this.afDb.createPushId()}`)
        .update(object)
        .then(() => {
          res(clientId);
        })
        .catch((e) => {
          rej(e);
        });
    });
  }

  public setRaffleConfig(object: any, clientId: string): any {
    object.update_at = new Date().getTime();
    return new Promise((res, rej) => {
      return firebase
        .database()
        .ref(`/Clients/${clientId}/RaffleConfig`)
        .update(object)
        .then(() => {
          res(clientId);
        })
        .catch((e) => {
          rej(e);
        });
    });
  }

  public setUsersRaffle(clientId: string, users: any): Promise<any> {
    // TODO: Deshabilitado xq firestore no permite eliminar en bloque
    // let db = firebase.firestore();
    // let batch = db.batch();
    // let ref;
    // let i = 1;

    // Object.keys(users).forEach(key => {
    //   if ((i % 500) !== 0) {
    //     ref = db.collection(clientId).doc('raffle_private').collection('users').doc(key);
    //     batch.set(ref, users[key])
    //   } else {
    //     ref = db.collection(clientId).doc('raffle_private').collection('users').doc(key);
    //     batch.set(ref, users[key])
    //     batch.commit();
    //     batch = db.batch();
    //   }
    //   i++;
    // });
    // batch.commit();
    // return new Promise(res => res(i - 1));

    return firebase
      .database()
      .ref(`/Clients/${clientId}/RafflePrivate`)
      .set(users);
  }

  public setClientSponsor(clientId: string, object: any, id: string = ""): any {
    if (!id) {
      id = this.afDb.createPushId();
    }

    object.id = id;

    return new Promise((res, rej) => {
      return firebase
        .database()
        .ref(`/ClientConfig/${clientId}/Sponsor/${id}`)
        .update(object)
        .then(() => {
          res(clientId);
        })
        .catch((e) => {
          rej(e);
        });
    });
  }

  public setUserAdminClient(userId: string, clientId: string): Promise<any> {
    return firebase
      .database()
      .ref(`/AdministratorList/${userId}`)
      .update({ managerClient: clientId })
      .then(() => clientId);
  }

  public setClientStaff(clientId: string, object: any, staffId: string = ""): any {
    if (!staffId) {
      staffId = this.afDb.createPushId();
    }

    object.id = staffId;

    return new Promise((res, rej) => {
      return firebase
        .database()
        .ref(`/Clients/${clientId}/Interactivity/Staff/${staffId}`)
        .update(object)
        .then(() => {
          res(clientId);
        })
        .catch((e) => {
          rej(e);
        });
    });
  }

  public async getPath(clientId: string, path: string) {
    const snap = await this.afDb
      .list<any>(`Clients/${clientId}/${path}`).query.once('value');
    return snap.val();
  }

  public async setLogoutUser(userId: string, clientId: string) {
    const data = { userId, setLogout: true }
    return this.afDb.database.ref(`Clients/${clientId}/UsersActions/${userId}`)
      .update(data).then(() => data)
      .catch((error) => {
        this.utilities.consoleError('Error realizando logout a usuario', error);
      });
  }

}
// end file



// WEBPACK FOOTER //
// ./src/providers/client.ts